// RestoreUndoToast — 荒らし復旧 UI 第 1 段階「元に戻す」トースト
// (vandalism-recovery-ui-2026-09-27.md §2「元に戻す」+ 追補「範囲の修正」)。
//
// **自分が要求した復元**の `whiteboard.restore` broadcast に `autoSavedPastCanvasId`
// (= rotate 直前の現行セッションを自動保存した過去キャンバス行の id) が載っていたら、
// 「元に戻す」ボタン付きトーストを出す。押すとその id で再度 `whiteboard.restore-request`
// を送る (= 今回の復元を取り消して直前の状態に戻す)。
//
// - `ev.uid === myUid` で「自分が要求した復元か」を判定する (broadcast の uid は
//   restore-request を送った actor そのもの、`WhiteboardRestoreService` が stamp する
//   ため他人の復元では鳴らない)。
// - `autoSavedPastCanvasId` が無い broadcast (id 未採番 / 記録対象が無い / 旧サーバ) では
//   ボタンを出さない (protocol コメント参照、`whiteboard.ts` `autoSavedPastCanvasId`)。
// - 失敗 (`whiteboard.restore-rejected`、記録が fire-and-forget なので行が実在しない
//   ことがある) は「元に戻せませんでした。過去キャンバスの一覧から戻してください」に
//   差し替える。
// - **「元に戻す」自体の結果は再オファーしない** (範囲の修正、2026-09-27 夜):
//   直前に自分が「元に戻す」で送った `pastCanvasId` そのものの broadcast が返って
//   きたら「元に戻しました」だけ出し、ボタンは出さない。ここでまた `autoSavedPast
//   CanvasId` を offer してしまうと、ボタンの意味が「誤復元のやり直し」に反転して
//   しまう (= 押すたびに直前の巻き戻しを繰り返す、ユーザの意図と逆)。
//   それ以外の自分の復元 (通常の「続きを描く」/「時点を選んで戻す」) は従来どおり
//   offer する — 「元に戻す」を繰り返し辿れる (chain) のはこちらの経路のみ。
//
// RoomLayout 直下に hoist して置く (= レイアウト非依存、AnnounceBanner / MaintenanceBanner
// と同じ位置付け)。canvas 中央に出す ImagepasteToastOverlay とは異なり、復元は
// 「今すぐ気づかなくても後から一覧に戻れる」性質の通知なので画面隅の据え置きトーストにする
// (作者確認なしの UX 判断。過去キャンバス一覧はいつでも開けるため見逃しても詰まない)。

import { useEffect, useRef, useState } from 'react';

import { restoreRejectedText } from '../application/pastCanvasList.js';
import type { Transport } from '../application/ports/Transport.js';
import { sendRestoreRequest } from '../application/useCases.js';

export interface RestoreUndoToastProps {
  readonly transport: Transport;
  /** 自分の uid (`RoomState.uid`)。broadcast の actor と突き合わせて自分の復元か判定する。 */
  readonly myUid: string;
}

interface Offer {
  readonly pastCanvasId: string;
}

export const RestoreUndoToast = ({ transport, myUid }: RestoreUndoToastProps): React.ReactNode => {
  const [offer, setOffer] = useState<Offer | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 「元に戻す」経由で自分が今まさに送っている restore-request の対象 pastCanvasId。
  // この id そのものの broadcast が返ってきたら「元に戻す」の成功、それ以外は普通の
  // 復元 (= 新たな offer の候補) と区別するための印。
  const pendingUndoPastCanvasIdRef = useRef<string | null>(null);
  // 直前の「元に戻す」が成功した (= 「元に戻しました」表示中、ボタンは出さない)。
  const [undone, setUndone] = useState(false);

  useEffect(() => {
    const off = transport.onEvent((ev) => {
      if (ev.t === 'whiteboard.restore') {
        if (ev.uid !== myUid) return; // 他人の復元では鳴らさない。
        setPending(false);
        setError(null);
        const wasMyUndo =
          pendingUndoPastCanvasIdRef.current !== null &&
          ev.pastCanvasId === pendingUndoPastCanvasIdRef.current;
        pendingUndoPastCanvasIdRef.current = null;
        if (wasMyUndo) {
          // 「元に戻す」自体の結果: 再オファーしない (上記コメント参照)。
          setOffer(null);
          setUndone(true);
          return;
        }
        setUndone(false);
        setOffer(
          ev.autoSavedPastCanvasId !== undefined
            ? { pastCanvasId: ev.autoSavedPastCanvasId }
            : null,
        );
        return;
      }
      if (ev.t === 'whiteboard.restore-rejected') {
        // 「元に戻す」経由の request 送信中だけ反応する (一覧モーダル側の
        // restore-request はそちらが自前で購読して処理するので二重表示しない)。
        setPending((wasPending) => {
          if (!wasPending) return wasPending;
          pendingUndoPastCanvasIdRef.current = null;
          setError(restoreRejectedText(ev.reason));
          setOffer(null);
          setUndone(false);
          return false;
        });
      }
    });
    return off;
  }, [transport, myUid]);

  const handleUndo = (): void => {
    if (offer === null || pending) return;
    setError(null);
    pendingUndoPastCanvasIdRef.current = offer.pastCanvasId;
    sendRestoreRequest(transport, offer.pastCanvasId);
    setPending(true);
  };

  const handleDismiss = (): void => {
    setOffer(null);
    setError(null);
    setUndone(false);
  };

  if (offer === null && error === null && !undone) return null;

  return (
    <div
      data-testid="restore-undo-toast"
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed right-4 bottom-4 z-50 flex justify-end"
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-lg border border-white/10 bg-[var(--color-panel)] px-4 py-2.5 text-sm text-[var(--color-text)] shadow-xl">
        {error !== null ? (
          <span className="text-[var(--color-error)]">{error}</span>
        ) : undone ? (
          <span>元に戻しました</span>
        ) : (
          <span>キャンバスを復元しました</span>
        )}
        {offer !== null && (
          <button
            type="button"
            onClick={handleUndo}
            disabled={pending}
            className="rounded bg-[var(--color-accent)] px-3 py-1 font-bold text-[var(--color-panel)] text-xs hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {pending ? '元に戻しています…' : '元に戻す'}
          </button>
        )}
        <button
          type="button"
          aria-label="閉じる"
          onClick={handleDismiss}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          ×
        </button>
      </div>
    </div>
  );
};
